import {useCallback, useMemo} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {useSearchParams} from 'react-router-dom'
import {QUERY_KEYS} from '@/queryClient'
import {NotificationCount, UserNotification} from '@/features/notifications/types'
import {notificationDetailQueryKey, useGetNotification} from '@/features/notifications/queries/useGetNotification'
import {updateNotificationsInCache} from '@/features/notifications/utils/cacheUtils'
import {useNotificationContextHostStore} from '@/features/notifications/store/useNotificationContextHostStore'
import {useNotificationsPanelStore} from '@/features/notifications/store/useNotificationsPanelStore'
import {useMarkNotification} from './useMarkNotification'

export const NOTIFICATION_ID_PARAM = 'notification_id'

export type NotificationWorkContextPhase = 'idle' | 'active' | 'confirmed'

export interface NotificationWorkContext {
    notification: UserNotification | undefined
    phase: NotificationWorkContextPhase
    isPending: boolean
    markHandled: () => void
    undo: () => void
    /** Ends the work context, leaving any hosting surface open */
    dismiss: () => void
    /** Ends the work context *and* the deep link of whichever surface is hosting the bar */
    dismissWithHost: () => void
}

type MarkContext = {previousCount: NotificationCount | undefined; previousNotification: UserNotification | undefined}

/**
 * The operator's active work context: the notification they opened, carried on the address as
 * `?notification_id=`, so it survives a reload, follows history navigation, is shareable, and
 * ends by itself as soon as they navigate somewhere without it.
 *
 * `phase` is derived from the notification's own state, never from what happened in this
 * sitting: a task that is already handled reads as handled whoever handled it, whenever — on a
 * reload, in a second tab, or when the operator simply reopens it from the list. Deriving it
 * from a local "just confirmed" flag instead would show an already-handled task as still
 * pending, offering to handle it a second time. There is no auto-timeout: the confirmed state
 * stands as long as the task is handled.
 *
 * It also reports `idle` while the notifications panel is open, so that every surface reading it
 * agrees at once: the bar renders nothing and a hosting surface keeps its own header instead of
 * reserving room for a bar that is not there.
 */
export const useNotificationWorkContext = (): NotificationWorkContext => {
    const [searchParams, setSearchParams] = useSearchParams()
    const queryClient = useQueryClient()
    const notificationId = searchParams.get(NOTIFICATION_ID_PARAM) ?? undefined

    // Hydrating from the id alone: an unresolvable id simply yields no notification and no bar.
    const {data: notification} = useGetNotification(notificationId)
    const notificationDetailKey = useMemo(() => notificationDetailQueryKey(notificationId), [notificationId])

    const hostSearchParams = useNotificationContextHostStore(state => state.hostSearchParams)

    // While the operator is looking at the queue itself there is no bar: the notification list
    // *is* the task list, so restating the current task above it is noise. The context is not
    // ended, only hidden — closing the panel brings the bar back.
    const isNotificationsPanelOpen = useNotificationsPanelStore(state => state.isOpen)

    const {mutate: markNotification, isPending} = useMarkNotification<MarkContext>({
        urlParams: {notificationId: notificationId ?? ''},
        options: {
            // Move the badge and the list the moment the operator acts, mirroring
            // useMarkNotificationAsRead, and roll both back if the request fails.
            onMutate: ({isRead}) => {
                const previousCount = queryClient.getQueryData<NotificationCount>([QUERY_KEYS.NOTIFICATION_COUNT])
                const previousNotification = queryClient.getQueryData<UserNotification>(notificationDetailKey)
                queryClient.setQueryData<NotificationCount>([QUERY_KEYS.NOTIFICATION_COUNT], oldData => {
                    if (!oldData) return oldData
                    return {
                        ...oldData,
                        toRead: isRead ? Math.max(0, oldData.toRead - 1) : oldData.toRead + 1
                    }
                })
                updateNotificationsInCache(queryClient, cached =>
                    cached.id === notificationId ? {...cached, readAt: isRead ? new Date() : null} : cached
                )
                // The bar reads the detail query, not the list, and updateNotificationsInCache
                // only ever touches the infinite pages — so without this the bar would keep
                // showing the old state until the refetch landed.
                queryClient.setQueryData<UserNotification>(notificationDetailKey, cached =>
                    cached ? {...cached, readAt: isRead ? new Date() : null} : cached
                )
                return {previousCount, previousNotification}
            },
            onError: (_error, _payload, context) => {
                // The bar stays actionable and the notification keeps its previous state, so the
                // bar, the list and the badge cannot end up disagreeing. The shared hook has
                // already told the operator, and the action can simply be retried.
                if (context?.previousCount) {
                    queryClient.setQueryData([QUERY_KEYS.NOTIFICATION_COUNT], context.previousCount)
                }
                if (context?.previousNotification) {
                    queryClient.setQueryData(notificationDetailKey, context.previousNotification)
                }
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.NOTIFICATIONS]})
            }
        }
    })

    const markHandled = useCallback(() => {
        if (!notificationId) return
        // `isRead` is always sent explicitly: the backend validator treats it as optional and
        // an omitted flag *clears* the state instead of setting it. Re-handling an already
        // handled notification just rewrites the timestamp, so it is safely idempotent.
        markNotification({isRead: true})
    }, [markNotification, notificationId])

    const undo = useCallback(() => {
        if (!notificationId) return
        markNotification({isRead: false})
    }, [markNotification, notificationId])

    // Every way of ending the context goes through one navigation. `setSearchParams` always
    // builds from the calling render's snapshot — its functional form included — so a second
    // writer in the same tick would restore whatever the first one removed. When a hosting
    // surface is involved, its deep-link params are therefore cleared here, not by the host.
    const clearWorkContext = useCallback(
        (alsoClearParams: string[]) => {
            if (!notificationId) return
            const nextSearchParams = new URLSearchParams(searchParams)
            nextSearchParams.delete(NOTIFICATION_ID_PARAM)
            alsoClearParams.forEach(param => nextSearchParams.delete(param))
            // `replace` so toggling a work context never fills the back stack.
            setSearchParams(nextSearchParams, {replace: true})
        },
        [notificationId, searchParams, setSearchParams]
    )

    const dismiss = useCallback(() => clearWorkContext([]), [clearWorkContext])

    const dismissWithHost = useCallback(() => clearWorkContext(hostSearchParams), [clearWorkContext, hostSearchParams])

    // Derived from the task's own state, so an already-handled notification opened again reads
    // as handled instead of offering to handle it a second time.
    const phase: NotificationWorkContextPhase =
        !notification || isNotificationsPanelOpen ? 'idle' : notification.readAt ? 'confirmed' : 'active'

    return {notification, phase, isPending, markHandled, undo, dismiss, dismissWithHost}
}
